/**
 * The strip's height. Lives next to the component because the two must agree:
 * the banner draws itself `h-env-banner` tall and the rest of the shell sizes
 * itself against the same variable.
 */
export const ENVIRONMENT_BANNER_HEIGHT = '1.75rem';

const CSS_VARIABLE = '--env-banner-height';

/**
 * Publishes (or clears) the space the environment banner occupies by setting
 * `--env-banner-height` on the document root.
 *
 * Call this synchronously BEFORE the React app mounts: the variable drives the
 * `body` margin and every full-height shell, so setting it during render would
 * shift the whole layout on the first paint.
 */
export function reserveEnvironmentBannerSpace(shouldShowBanner: boolean): void {
  const root = document.documentElement;

  if (shouldShowBanner) {
    root.style.setProperty(CSS_VARIABLE, ENVIRONMENT_BANNER_HEIGHT);
    return;
  }

  // Explicitly remove rather than set '0rem', so the token's own fallback
  // applies and nothing depends on this function having run.
  root.style.removeProperty(CSS_VARIABLE);
}

/**
 * The reserved banner height in px, or 0 when no banner is shown. For code that positions
 * things in px against the window (popover collision padding), where the CSS token cannot reach.
 */
export function readEnvironmentBannerHeightPx(): number {
  const root = document.documentElement;
  const value = root.style.getPropertyValue(CSS_VARIABLE).trim();
  if (!value) return 0;

  const rootFontPx = Number.parseFloat(getComputedStyle(root).fontSize) || 16;
  const amount = Number.parseFloat(value);
  return value.endsWith('rem') ? amount * rootFontPx : amount;
}
